import { FC } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Box,
  Button,
  FormHelperText,
  Typography,
  FormControl,
  OutlinedInput
} from '@mui/material';
import TermsOfUseText from './TermsOfUseText';
import OdForm, { OnSubmit } from '../formik/OdForm';
import TextField from '../formik/TextField';
import { useSimpleSaveAction } from '~/controller/SaveContextProvider';
import { InitPasswordConfirmInput } from '~/gen/api/user';
import { useDispatch, useSelector } from 'react-redux';
import { getUserActivateAutoLoginError, raiseUserActivateAutoLogin } from '~/actions/activate';
import { ReduxState } from 'ra-core';
import { rPassword } from '~/pages/authentication/LoginContext';


const validationSchema = 
  Yup.object().shape({
    password: Yup.string().min(12).max(255)
      .matches(
        rPassword, 
        'Password must contain uppercase and lowercase characters, numbers and special characters')
      .required('Password is required'),
    confirmPassword: Yup.string().min(12).max(255)
      .matches(
        rPassword, 
        'Password must contain uppercase and lowercase characters, numbers and special characters')
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirm Password is required'),
  })

const ActivateAccountForm: FC<{
  code: string | null,
  email: string | null,
  cid: string | null,
}> = ({ code, email, cid, }) => {
  const dispatch = useDispatch()
  const initialValues = {
    password: '',
    confirmPassword: '',
    submit: null
  }

  const sendConfirm = useSimpleSaveAction()

  const onSubmit: OnSubmit<typeof initialValues> = (values, { setSubmitting, setStatus, setErrors }) => {
    if (code && email) {
      const options = {
        onSuccess: () => {
          setStatus({ success: true })
          setSubmitting(false)
          dispatch(raiseUserActivateAutoLogin(
            email,
            values.password,
            code,
            'RegisterActivate',
          ))
        },
        onFailure: (err: any) => {
          setStatus({ success: false })
          setErrors({ submit: err.message })
          setSubmitting(false)
        }
      }

      const payload: InitPasswordConfirmInput = {
        code,
        ...values,
      }
      sendConfirm({...payload, cid}, options)
    }
  }

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, isSubmitting, setFieldError } = formikCtrl

  useSelector<ReduxState, any>(store => {
    const autoLoginError = getUserActivateAutoLoginError(store)
    if (autoLoginError && errors.submit !== autoLoginError) {
      setFieldError('submit', autoLoginError)
    }
  })
  
  return (
    <Box>
      <Typography color="textPrimary" variant="h6" sx={{ pb: 1 }} >
        Email Address
      </Typography>
      <FormControl variant="outlined" fullWidth sx={{ pb: 3 }} >
        <OutlinedInput 
          value={email}
          disabled
          inputProps={{ 'aria-label': 'email address' }}
        />
      </FormControl>
      <OdForm formikCtrl={formikCtrl}>
        <Typography color="textPrimary" variant="h6" sx={{ pb: 0 }} >
          Choose a Password
        </Typography>
        <TextField
          label="Password"
          margin="normal"
          name="password"
          type="password"
          />
        <TextField
          label="Confirm Password"
          margin="normal"
          name="confirmPassword"
          type="password"
        />
        <Box sx={{ alignItems: 'center', display: 'flex', ml: -1, mt: 2 }} >
        </Box>
        {errors.submit && (
          <Box sx={{ mt: 3 }}>
            <FormHelperText error>
              {errors.submit}
            </FormHelperText>
          </Box>
        )}
        <Box sx={{ mt: 2 }}>
          <Button
            color="primary"
            disabled={isSubmitting}
            fullWidth
            size="large"
            type="submit"
            variant="contained"
          >
            Submit
          </Button>
        </Box>
        <TermsOfUseText leadingText="By creating an account, you agree to OnData's" TextProps={{ mt: 3 }} />
      </OdForm>
    </Box>
  );
};

export default ActivateAccountForm;
